import { useEffect, useState } from "react";
import { Copy, Plus, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { PAYMENTS } from "@/constants/testIds";
import SquareChargeModal from "@/components/payments/SquareChargeModal";

const LINK_STATUS_LABELS = { pending: "Pendiente", completed: "Pagado", cancelled: "Cancelado" };
const LINK_STATUS_COLORS = { pending: "bg-[#F4E4D6] text-[#B5793A]", completed: "bg-[#E3EDE1] text-[#5B7A54]", cancelled: "bg-muted text-muted-foreground" };

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
}

/** Página de Pagos: generar cobros (link o presencial) para cualquier
 * paciente de la clínica, y ver el historial de links y cobros ya
 * procesados con Square. Admin ve todo; Profesional solo sus pacientes. */
export default function PaymentsPage() {
  const [links, setLinks] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const { toast } = useToast();
  // Fix AW (2026-10-27): un rol personalizado con "Pagos" solo mira; cobrar sigue siendo de admin y profesional
  const { user } = useAuth();
  const canCharge = user?.role !== "custom";

  const load = async () => {
    setLoading(true);
    try {
      const [linksRes, historyRes] = await Promise.all([
        api.get("/payments/square/links"),
        api.get("/payments/square/history"),
      ]);
      setLinks(linksRes.data);
      setHistory(historyRes.data);
    } catch {
      // el card individual muestra el error de conexion si aplica
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCopy = async (url) => {
    await navigator.clipboard.writeText(url);
    toast({ title: "Enlace copiado" });
  };

  return (
    <div className="space-y-8" data-testid={PAYMENTS.page}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Pagos</h1>
          <p className="text-base text-muted-foreground mt-1">Links de cobro, cobros presenciales y recibos con Square.</p>
        </div>
        {canCharge && (
          <Button className="bg-brand hover:bg-brand-hover text-white gap-2" onClick={() => setModalOpen(true)} data-testid={PAYMENTS.newChargeButton}>
            <Plus className="w-4 h-4" /> Nuevo cobro
          </Button>
        )}
      </div>

      <div>
        <h2 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium mb-3">Links de cobro</h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando...</p>
        ) : links.length === 0 ? (
          <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid={PAYMENTS.linksEmptyState}>
            <p className="text-sm text-muted-foreground">Todavía no generaste ningún link de cobro.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid={PAYMENTS.linksTable}>
            {links.map((l) => (
              <div key={l.id} className="bg-white border border-border rounded-xl p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`${PAYMENTS.linkRowPrefix}${l.id}`}>
                <div>
                  <p className="text-sm font-medium text-foreground">{l.patient_name} &middot; ${l.amount.toFixed(2)} {l.is_deposit && <span className="text-muted-foreground">(sena)</span>}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(l.created_at)}{l.note ? ` \u00b7 ${l.note}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${LINK_STATUS_COLORS[l.status]} border-none font-normal`} data-testid={`${PAYMENTS.linkStatusBadgePrefix}${l.id}`}>{LINK_STATUS_LABELS[l.status]}</Badge>
                  <Button variant="outline" size="icon" onClick={() => handleCopy(l.url)} data-testid={`${PAYMENTS.linkCopyButtonPrefix}${l.id}`}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium mb-3">Cobros procesados</h2>
        {loading ? null : history.length === 0 ? (
          <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid={PAYMENTS.paymentsEmptyState}>
            <p className="text-sm text-muted-foreground">Todavía no hay cobros registrados.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid={PAYMENTS.paymentsTable}>
            {history.map((p) => (
              <div key={p.id} className="bg-white border border-border rounded-xl p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`${PAYMENTS.paymentRowPrefix}${p.id}`}>
                <div>
                  <p className="text-sm font-medium text-foreground">{p.patient_name} &middot; ${p.amount.toFixed(2)} {p.is_deposit && <span className="text-muted-foreground">(sena)</span>}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(p.paid_at)} &middot; {p.provider === "square" ? "Square" : "Manual"}</p>
                </div>
                {p.receipt_url && (
                  <a href={p.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand underline" data-testid={`${PAYMENTS.paymentReceiptLinkPrefix}${p.id}`}>
                    <Receipt className="w-3.5 h-3.5" /> Recibo
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {canCharge && (
        <SquareChargeModal
          open={modalOpen}
          onOpenChange={(v) => { setModalOpen(v); if (!v) load(); }}
          onCreated={load}
        />
      )}
    </div>
  );
}
